import { supabase } from './supabaseClient';

// Page de chantiers (du plus récent au plus ancien) : `offset` premiers ignorés, `limit` au plus.
// Renvoie aussi le total, pour savoir s'il reste des chantiers à afficher.
export async function getChantiersForUser(userId, { offset = 0, limit = 9 } = {}) {
  const { data: memberships, error: memberError } = await supabase
    .from('chantier_members')
    .select('chantier_id')
    .eq('user_id', userId)
    .is('deleted_at', null);

  if (memberError) throw memberError;

  const memberChantierIds = (memberships || []).map((m) => m.chantier_id);

  let query = supabase
    .from('chantiers')
    .select(`
      *,
      owner:owner_id(id, name, avatar_url, role),
      chantier_type_data:chantier_type_id(id, name, color, icon),
      plans:chantier_plans(id, deleted_at)
    `, { count: 'exact' })
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    // départage stable : deux chantiers créés au même instant ne changent pas de page
    .order('id', { ascending: false })
    .range(offset, offset + limit - 1);

  query =
    memberChantierIds.length > 0
      ? query.or(`owner_id.eq.${userId},id.in.(${memberChantierIds.join(',')})`)
      : query.eq('owner_id', userId);

  const { data, count, error } = await query;
  if (error) throw error;
  return { chantiers: data || [], total: count ?? 0 };
}

export async function getChantierMembers(chantierId) {
  const { data, error } = await supabase
    .from('chantier_members')
    .select(`
      user_id,
      member_role,
      user:user_id(id, name, avatar_url)
    `)
    .eq('chantier_id', chantierId)
    .is('deleted_at', null)
    .order('joined_at', { ascending: true });

  if (error) throw error;

  // Un membre invité sur plusieurs lots a une ligne chantier_members par lot :
  // on ne garde qu'une entrée par utilisateur (l'avatar n'a pas besoin des lots).
  const byUser = new Map();
  for (const row of data || []) {
    if (row.user && !byUser.has(row.user_id)) byUser.set(row.user_id, { ...row.user, member_role: row.member_role });
  }
  return Array.from(byUser.values());
}

// Phases de projet, dans l'ordre (enum chantier_phase). `color` sert à la barre et au fond du
// badge, `textColor` au texte du badge.
export async function isChantierOwner(chantierId) {
  const { data, error } = await supabase.rpc('is_chantier_owner', { p_chantier_id: chantierId });

  if (error) throw error;
  return data === true;
}

export const CHANTIER_PHASES = [
  { value: 'prospect', label: 'Prospection', color: '#9CA3AF', textColor: '#1a1c1e' },
  { value: 'proposition', label: 'Proposition', color: '#8B5CF6', textColor: '#ffffff' },
  { value: 'projet', label: 'Projet', color: '#F6A500', textColor: '#1a1c1e' },
  { value: 'cloture', label: 'Clôturé', color: '#10B981', textColor: '#1a1c1e' },
];

export function getPhaseIndex(phase) {
  const index = CHANTIER_PHASES.findIndex((p) => p.value === phase);
  return index === -1 ? 0 : index;
}

export async function updateChantierPhase(chantierId, phase) {
  const { error } = await supabase
    .from('chantiers')
    .update({ phase, updated_at: new Date().toISOString() })
    .eq('id', chantierId);

  if (error) throw error;
}

export async function createChantier(userId, { name, address, postal_code, city, photo_url, phase }) {
  const { data, error } = await supabase
    .from('chantiers')
    .insert({
      owner_id: userId,
      name,
      address,
      postal_code,
      city,
      photo_url,
      phase,
      status: 'planning',
    })
    .select()
    .single();

  if (error) throw error;

  // L'ajout du owner dans chantier_members est géré par le trigger
  // add_owner_as_chantier_member (AFTER INSERT ON chantiers), dans la même
  // transaction que l'INSERT ci-dessus — même comportement que l'app mobile.

  return data;
}

export async function insertChantierDocuments(chantierId, userId, documents) {
  if (documents.length === 0) return [];

  const { data, error } = await supabase
    .from('chantier_documents')
    .insert(
      documents.map((doc) => ({
        chantier_id: chantierId,
        uploaded_by: userId,
        doc_type: doc.doc_type,
        file_name: doc.file_name,
        storage_path: doc.storage_path,
        lot_extraction_status: 'pending',
      }))
    )
    .select();

  if (error) throw error;
  return data || [];
}
